import type { FunctionComponent } from 'react';

import { Grid, Group, NumberInput, Stack, Switch, Tooltip } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { useTranslation } from 'i18n';

import { MODIFIER_MAXIMUM } from 'constants/menuManagement';

import { ClearInputButton } from 'components/MenuManagement/ClearInputButton';
import { RIGHT_SECTION_WIDTH } from 'components/MenuManagement/inputConstants';

const NS = 'menuManagementFields';

const INDIVIDUAL_MIN = 'individualMinimum';
const INDIVIDUAL_MAX = 'individualMaximum';
const MULTI_MODS_ENABLED = 'multipleModifiersEnabled';

export type MultipleModifiersFormValues = {
  multipleModifiersEnabled: boolean;
  individualMinimum: string | number | null;
  individualMaximum: string | number | null;
};

type MultipleModifiersCheckboxFieldProps = {
  form: UseFormReturnType<MultipleModifiersFormValues>;
};

export const MultipleModifiersCheckboxField: FunctionComponent<
  MultipleModifiersCheckboxFieldProps
> = ({ form }) => {
  const { t } = useTranslation(NS);

  return (
    <Stack>
      <Tooltip label={t('MultipleModifiersCheckboxField.tooltipLabel')}>
        <Group mb={!form.values[MULTI_MODS_ENABLED] ? 'lg' : 0}>
          <Switch
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...form.getInputProps(MULTI_MODS_ENABLED, { type: 'checkbox' })}
            onChange={event => {
              form.setFieldValue(MULTI_MODS_ENABLED, event.currentTarget.checked);
              if (!event.currentTarget.checked) {
                form.setFieldValue(INDIVIDUAL_MIN, null);
                form.setFieldValue(INDIVIDUAL_MAX, null);
              }
            }}
            label={t('MultipleModifiersCheckboxField.switchLabel')}
          />
        </Group>
      </Tooltip>

      {!!form.values[MULTI_MODS_ENABLED] && (
        <Grid gutter="xs">
          <Grid.Col span={6}>
            <NumberInput
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...form.getInputProps(INDIVIDUAL_MIN)}
              value={form.values[INDIVIDUAL_MIN] ?? ''}
              label={t('MultipleModifiersCheckboxField.individualMinLabel')}
              size="sm"
              required
              max={MODIFIER_MAXIMUM}
              onChange={value => form.setFieldValue(INDIVIDUAL_MIN, value)}
              rightSectionWidth={RIGHT_SECTION_WIDTH}
              rightSection={
                <ClearInputButton
                  onClick={() => form.setFieldValue(INDIVIDUAL_MIN, null)}
                  show={!!form.values[INDIVIDUAL_MIN]}
                />
              }
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <NumberInput
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...form.getInputProps(INDIVIDUAL_MAX)}
              value={form.values[INDIVIDUAL_MAX] ?? ''}
              label={t('MultipleModifiersCheckboxField.individualMaxLabel')}
              size="sm"
              required
              max={MODIFIER_MAXIMUM}
              onChange={value => form.setFieldValue(INDIVIDUAL_MAX, value)}
              rightSectionWidth={RIGHT_SECTION_WIDTH}
              rightSection={
                <ClearInputButton
                  onClick={() => form.setFieldValue(INDIVIDUAL_MAX, null)}
                  show={!!form.values[INDIVIDUAL_MAX]}
                />
              }
            />
          </Grid.Col>
        </Grid>
      )}
    </Stack>
  );
};
